import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import { getErrorMessage, parseTimeStamp } from "../../../helpers/functions";
import { useSnackbar } from "notistack";
import CaseService from "../../../services/CaseService";
import moment from "moment";
import MoreOrLess from "../../common/MoreOrLess";
import queryString from "query-string";
import { sanitize } from "dompurify";

export default function ScratchPadWidget({
  id,
  type = "Mediator",
  disabled = false,
  viewOnly,
  modal,
  setModal,
}) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const scrollRef = useRef();

  useEffect(() => {
    if (modal !== undefined) {
      setOpen(modal);
    }
  }, [modal]);

  const addNotes = async (value) => {
    if (!value?.trim()) return;
    try {
      setIsLoading(true);
      const payload = { content: value };
      const res = await CaseService.addNotes(payload, id);
      if (res?.message) {
        enqueueSnackbar(res?.message, { variant: "success" });
        setNotes((prev) => [res?.newNote, ...prev]);
        setNote("");
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    async function getAllNotes() {
      if (!id) return;

      setIsLoading(true);
      try {
        let params = { page: 1, perPage: 1000 };
        let stringParams = `?${queryString.stringify(params)}`;

        const res = await CaseService.getNotes(id, stringParams);
        setNotes(res?.data || []);
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error), { variant: "error" });
        setNotes([]);
      } finally {
        setIsLoading(false);
      }
    }

    if (id && open) {
      getAllNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, open]);

  const toggleOpen = () => {
    setOpen((prev) => {
      const nextOpen = !prev;
      if (setModal) {
        setModal(nextOpen);
      }
      return nextOpen;
    });
  };

  return (
    <WidgetRoot>
      {open && (
        <Panel role="dialog" aria-label="JustAct Scratch Pad">
          <PanelHeader>
            <HeaderTop>
              <BrandRow>
                <HeaderText>
                  <BrandText>Scratch Pad</BrandText>
                  <SubBrandText>
                    {notes?.length || 0}{" "}
                    {notes?.length === 1 ? "note" : "notes"}
                  </SubBrandText>
                </HeaderText>
              </BrandRow>
              <IconButton onClick={toggleOpen} aria-label="Close Scratch Pad">
                <CloseIcon />
              </IconButton>
            </HeaderTop>
          </PanelHeader>

          <NoteContainer ref={scrollRef}>
            {isLoading ? (
              <LoadingContainer>
                <Spinner />
                <LoadingText>Loading your notes...</LoadingText>
              </LoadingContainer>
            ) : notes?.length > 0 ? (
              <NotesWrapper>
                {notes?.map((n, index) => (
                  <NoteCard key={n?.id || index}>
                    <NoteHeader>
                      <TimeStamp>
                        <TimeText>
                          {moment(parseTimeStamp(n?.created_at)).format(
                            "DD MMM YYYY",
                          )}
                          <TimeSeparator>•</TimeSeparator>
                          {moment(parseTimeStamp(n?.created_at)).format(
                            "hh:mm A",
                          )}
                        </TimeText>
                      </TimeStamp>
                    </NoteHeader>
                    <NoteContent>
                      <MoreOrLess
                        Paragraph={
                          <ContentText
                            dangerouslySetInnerHTML={{
                              __html: sanitize(n?.content),
                            }}
                          />
                        }
                        className="content"
                      />
                    </NoteContent>
                  </NoteCard>
                ))}
              </NotesWrapper>
            ) : (
              <EmptyStateContainer>
                <EmptyMsg>No notes yet</EmptyMsg>
                <EmptySubtext>
                  Start adding notes to keep track of important information
                </EmptySubtext>
              </EmptyStateContainer>
            )}
          </NoteContainer>

          {viewOnly !== true && (
            <Composer>
              <ComposerInput
                variant="outlined"
                className={"input-height-auto"}
                multiline={true}
                rows={1}
                placeholder="Type your note here..."
                // maxRows={3}
                name={"note"}
                value={note}
                disabled={disabled}
                onChange={(e) => setNote(e.target.value)}
              />
              <SendButton
                onClick={() => addNotes(note)}
                disabled={!note?.trim() || disabled}
                aria-label="Send message"
              >
                <SendIcon />
              </SendButton>
            </Composer>
          )}

          {/* {viewOnly !== true && (
            <EditorContainer>
              <EditorWrapper>
                <EditorLabel>
                  <LabelText>Add a new note</LabelText>
                </EditorLabel>
                <InputWrapper>
                  <CustomInputField
                    variant="outlined"
                    className={"input-height-auto"}
                    multiline={true}
                    // rows={3}
                    placeholder="Type your note here..."
                    // maxRows={3}
                    name={"note"}
                    value={note}
                    disabled={disabled}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </InputWrapper>
                <ButtonWrapper>
                  <PrimaryCTAButton
                    onClick={() => addNotes(note)}
                    disabled={!note?.trim() || disabled}
                  >
                    Add Note
                  </PrimaryCTAButton>
                </ButtonWrapper>
              </EditorWrapper>
            </EditorContainer>
          )} */}
        </Panel>
      )}

      <BubbleButton onClick={toggleOpen} aria-label="Open JustAct Scratch Pad">
        {open ? <CloseIcon light /> : <ScratchPadIcon />}
      </BubbleButton>
    </WidgetRoot>
  );
}

function ScratchPadIcon() {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 7H16"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 12H16"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 17H13"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17 14L19 16L17 18"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon({ light }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M18 6 6 18M6 6l12 12"
        stroke={light ? "#fff" : COLORS.COLOR_DARK}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f0f0f0;
  border-top: 4px solid ${COLORS?.BTN_GREEN || "#00838c"};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 16px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 20px;
`;

const LoadingText = styled.div`
  color: #666;
  font-size: 14px;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const WidgetRoot = styled.div`
  position: fixed;
  right: 15px;
  bottom: 24px;
  z-index: 1300;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 16px;

  @media (max-width: 480px) {
    right: 12px;
    bottom: 12px;
  }
`;

const BubbleButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: ${COLORS?.BTN_GREEN || "#00838c"};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 131, 140, 0.35);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: scale(1.06);
    box-shadow: 0 10px 28px rgba(0, 131, 140, 0.45);
  }

  &:active {
    transform: scale(0.97);
  }
`;

const Panel = styled.div`
  width: 480px;
  height: 538px;
  max-height: 75vh;
  background: linear-gradient(to bottom, #fafafa 0%, #ffffff 100%);
  border-radius: 16px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideUp} 0.22s ease-out;

  @media (max-width: 480px) {
    width: calc(100vw - 24px);
    height: 70vh;
  }
`;

const PanelHeader = styled.div`
  background: #293461;
  padding: 18px 18px 16px;
  flex-shrink: 0;
  border-bottom: 2px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const BrandText = styled.span`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 16px;
  font-weight: 800;
  color: ${COLORS.PRIMARY_WHITE};
`;

const SubBrandText = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  color: ${COLORS.PRIMARY_WHITE};
`;

const IconButton = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #e4e4e4;
  }
`;

const NoteContainer = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;

    &:hover {
      background: #a8a8a8;
    }
  }
`;

const NotesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const NoteCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 14px 16px;
  border: 2px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 131, 140, 0.12);
  }
`;

const NoteHeader = styled.div`
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
`;

const TimeStamp = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TimeText = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.INPUT_LABEL};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TimeSeparator = styled.span`
  color: #ccc;
`;

const NoteContent = styled.div`
  & .content {
    font-family: ${theme.fonts.primaryFontSemiBold};
    font-size: 13px;
    font-weight: 500;
    line-height: 1.6;
    color: ${COLORS.COLOR_DARK};
    word-break: break-word;

    & .readMoreText {
      color: ${COLORS?.BTN_GREEN} !important;
      font-weight: 600;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const ContentText = styled.div`
  p {
    margin: 6px 0;
  }

  ul,
  ol {
    margin: 6px 0;
    padding-left: 18px;
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 20px;
  text-align: center;
`;

const EmptyMsg = styled.div`
  color: ${COLORS.COLOR_DARK};
  font-weight: 700;
  font-size: 18px;
  margin-bottom: 8px;
  font-family: ${theme.fonts.primaryFontBold};
`;

const EmptySubtext = styled.div`
  color: #999;
  font-size: 13px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  max-width: 260px;
  line-height: 1.5;
`;

const Composer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  padding: 12px 14px;
  border-top: 2px solid #f0f0f0;
  background: #fff;
  flex-shrink: 0;
`;

const SendButton = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  flex-shrink: 0;
  background: ${COLORS?.BTN_GREEN || "#00838c"};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: scale(1.06);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ComposerInput = styled.textarea`
  flex: 1;
  resize: none;
  border: 2px solid #e8e8e8;
  border-radius: 10px;
  padding: 10px 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: ${COLORS.COLOR_DARK};
  outline: none;
  max-height: 80px;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${COLORS?.BTN_GREEN || "#00838c"};
  }

  &::placeholder {
    color: #999;
  }
`;
